/*
  This page facilitates communication with an AI assistant.
*/
import { useState, useCallback, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import {
  AssistantAllButtonTypes,
  CodeFile,
  languageType,
  Message,
} from "utils/interfaces";
import ChatMessages from "./ChatMessages";
import ChatMessageInput from "./ChatMessageInput";
import { markCodeAssistant, queryAssistant } from "services/assistantRequests";
import { getChatMessages } from "services/apiRequests";
import { EditorContext } from "components/utils/Contexts";
import { usePlatform } from "components/utils/PlatformContext";

interface AssistantParams {
  files?: CodeFile[] | undefined;
  AIAssistant: {
    name: string;
    img: string;
    greeting: string;
    intro: string;
  };
  returnToIDE: () => void;
  moveToNextLesson: () => void;
  newMessage: string | undefined;
  setNewMessage: React.Dispatch<React.SetStateAction<AssistantAllButtonTypes>>;
  language: languageType | null;
}

const defaultMessages = [
  "I'm stuck, Can you help me?",
  "Please check my code",
  "I don't understand",
  "I can see an error",
];

const externalEditorDefaultMessages = {
  "I don't understand":
    "I don't understand this lesson. Can you please provide me some help?",
  "Coding mistakes to avoid?": "What coding mistakes should I look out for?",
  "My code doesn't work": "What should I do if my code doesn't work?",
};

const Assistant: React.FC<AssistantParams> = ({
  files,
  AIAssistant,
  returnToIDE,
  moveToNextLesson,
  newMessage,
  setNewMessage,
  language,
}) => {
  // States definition
  const [waitingForAiResponse, setWaitingForAiResponse] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [showLoading, setShowLoading] = useState(false);

  // Get the context element
  const { assistantWatermark1, assistantWatermark2 } = usePlatform();
  const { userFirstName, userLastName } = useContext(EditorContext);

  // Get the url parameters
  const { moduleOrProject, id } = useParams();

  // First message definition
  const firstMessage: Message = {
    content: `## ${AIAssistant.greeting}\n\n${AIAssistant.intro}`,
    role: "assistant",
  };

  // Name initials definition
  const nameInitials =
    userFirstName && userLastName ? userFirstName[0] + userLastName[0] : "-";

  /**
   * Get the messages at the beginning of the function
   */
  useEffect(() => {
    getMessages();
  }, []);

  /**
   * Function used to set the timer for waiting for AI response
   */
  useEffect(() => {
    if (waitingForAiResponse) {
      const timer = setTimeout(() => {
        if (waitingForAiResponse) {
          setShowLoading(true);
        }
      }, 750);
      return () => clearTimeout(timer);
    } else {
      setShowLoading(false);
    }
  }, [waitingForAiResponse]);

  /**
   * Function used to update the messages by prepending the
   * predefined first message
   * @param messages
   */
  const updateMessages = (messages: Message[]) => {
    setMessages([firstMessage, ...messages]);
  };

  /**
   * Function used to get the chat messages
   * @returns
   */
  const getMessages = async () => {
    // Validate moduleOrProject value
    if (!(moduleOrProject && ["modules", "projects"].includes(moduleOrProject)))
      return;
    // Check the id value
    if (!id) return;

    // Get the chat messages
    const [error, existingMessages] = await getChatMessages(
      moduleOrProject,
      id,
    );

    if (existingMessages) updateMessages(existingMessages);

    // Check if there's a new message
    if (newMessage && newMessage !== "chat") {
      handleUserMessage(newMessage);
      setNewMessage("chat");
    }
  };

  const getAiMarks = useCallback(async () => {
    const [error, messages] = await markCodeAssistant(
      "Please check my code",
      files === undefined || files.length === 0 ? "" : files[0].content,
      id || "",
      moduleOrProject || "",
    );

    // Check if there are new messages
    if (messages) updateMessages(messages);
    // if (complete) {

    //   setMessages((prev) => [...prev, {
    //     content: message || "An error has occurred...",
    //     role: "assistant",
    //     complete: true
    //   }]);
    // } else {
    //   setMessages((prev) => [...prev, {
    //     content: message || "An error has occurred...",
    //     role: "assistant"
    //   }]);
    // }
  }, [files, messages, id]);

  const getAiResponse = useCallback(
    async (message: string) => {
      const [error, newMessages] = await queryAssistant(
        message,
        files && files.length > 0 && files[0].content ? files[0].content : "",
        id || "",
        moduleOrProject || "",
      );

      // Check if there are new messages
      if (newMessages) updateMessages(newMessages);
      // setMessages((prev) => [...prev, {
      //   content: text || "An error has occurred...",
      //   role: "assistant"
      // }]);
    },
    [files, messages, id],
  );

  /**
   * For sending messages via the conversation starters
   * @param text
   * @returns
   */
  const handleUserMessage = async (text: string) => {
    // Prevent send the message if the text is empty
    if (!text) return;

    // Check if it's already performing a request
    if (waitingForAiResponse) return;

    // Lock the function
    setWaitingForAiResponse(true);

    // Update the messages
    setMessages((prev) => [...prev, { content: text, role: "user" }]);

    // Check the nature of the question
    if (text === "Please check my code") {
      await getAiMarks();
    } else {
      await getAiResponse(text);
    }

    // Unlock the function
    setWaitingForAiResponse(false);
    setShowLoading(false);
  };

  return (
    <div className="w-full h-full px-4 pb-12 flex flex-col gap-6 z-10 items-center">
      {/* Watermarks */}
      {assistantWatermark1 && (
        <img
          src={assistantWatermark1}
          alt="Watermark 1"
          className="fixed top-0 right-0 w-72 h-72 -z-10"
        />
      )}
      {assistantWatermark2 && (
        <img
          src={assistantWatermark2}
          alt="Watermark 2"
          className="fixed bottom-0 left-0 w-72 h-72 -z-10"
        />
      )}
      {/* List of messages */}
      <ChatMessages
        messages={messages}
        showLoading={showLoading}
        AIAssistant={AIAssistant}
        returnToIDE={returnToIDE}
        moveToNextLesson={moveToNextLesson}
        language={language}
        nameInitials={nameInitials}
      />
      <div className="inline-flex flex-col items-center gap-4 h-fit max-w-[767px]">
        {language !== "Video and PDF" && (
          <div className="w-full inline-flex gap-2 overflow-x-hidden flex-wrap">
            {["Minecraft", "Micro:bit"].includes(language || "")
              ? Object.keys(externalEditorDefaultMessages).map((key, index) => (
                  <div
                    key={index}
                    onClick={() =>
                      handleUserMessage(
                        externalEditorDefaultMessages[
                          key as keyof typeof externalEditorDefaultMessages
                        ],
                      )
                    }
                    className={`group px-3 py-1 bg-neutral-50 hover:bg-neutral-100 ease-in-out duration-150 rounded-xl border border-secondary-600 flex-shrink-0 whitespace-nowrap select-none ${
                      waitingForAiResponse ? "cursor-default" : "cursor-pointer"
                    }`}
                  >
                    <div className="text-secondary-600 text-xs font-semibold font-poppins">
                      {key}
                    </div>
                  </div>
                ))
              : defaultMessages.map((text, index) => (
                  <div
                    key={index}
                    onClick={() => handleUserMessage(text)}
                    className={`group px-3 py-1 bg-neutral-50 hover:bg-neutral-100 ease-in-out duration-150 rounded-xl border border-secondary-600 flex-shrink-0 whitespace-nowrap select-none ${
                      waitingForAiResponse ? "cursor-default" : "cursor-pointer"
                    }`}
                  >
                    <div className="text-secondary-600 text-xs font-semibold font-poppins">
                      {text}
                    </div>
                  </div>
                ))}
          </div>
        )}
        {/* Chat text input */}
        <ChatMessageInput
          inputEnabled={waitingForAiResponse}
          handleSend={handleUserMessage}
        />
        {/* Footer message */}
        <p className="w-[95%] text-[10px] text-neutral-700 text-center">
          This website uses AI (like a smart helper) to make learning to code
          easier and more fun. Sometimes, the AI might make mistakes or say
          things that aren't quite right. That's because it's still learning
          too! If something looks odd or doesn't make sense, ask a teacher,
          adult, or trusted helper. Please don't type your name, address, phone
          number, or any other personal information into the chat. We work hard
          to make the AI safe and fair, and we keep checking how it works to
          make it better for everyone.
        </p>
      </div>
    </div>
  );
};

export default Assistant;
